import { saveAs } from 'file-saver'

import type { ProductSourceFeed } from '@DataManagement/types/product'

import type {
  PaginatedQueryParams,
  PaginatedResponse,
  ShopifyPaginatedQueryParams,
} from '../types'
import type { PriceAPIResponse } from '../types/batch-import'
import type { CreateProductDto } from '../types/dto/product/createProductDto'
import type {
  BatchImportActivity,
  CreateImportRequest,
  Filter,
  LatestBatchImportActivity,
  Product,
  ProductFilter,
  ProductSite,
} from '../types/product'
import type {
  ShopifyCountRequest,
  ShopifyCountResponse,
  ShopifyProductImportRequest,
  ShopifyProducts,
} from '../types/shopify-products'

import { baseApi } from '.'

export const productApiController = new AbortController()

type FeedTargetBody = {
  path: string
  authType?: string | null
  authPayload?: Record<string, unknown> | null
  syncPeriod?: string | null
  syncEnabled?: boolean
}
const productApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    products: builder.query<PaginatedResponse<Product>, PaginatedQueryParams>({
      query: (arg: PaginatedQueryParams) => ({
        url: '/products',
        method: 'GET',
        params: arg,
        signal: productApiController.signal,
      }),
      transformResponse: (response: PaginatedResponse<any>) => {
        response.data = response.data.map((i) => ({
          ...i,
          is_mine_tracker: i.tracker.find((t: any) => t.is_mine),
        }))
        return response
      },
      providesTags: ['Products'],
    }),
    totalProductCount: builder.query<number, void>({
      query: () => ({
        url: '/products/count',
        method: 'GET',
      }),
      providesTags: ['ProductCount'],
    }),
    shopifyProducts: builder.query<
      ShopifyProducts,
      ShopifyPaginatedQueryParams
    >({
      query: (arg: ShopifyPaginatedQueryParams) => ({
        url: '/shopify/products',
        method: 'GET',
        params: arg,
      }),
    }),
    shopifyVariantCount: builder.query<
      ShopifyCountResponse,
      ShopifyCountRequest | void
    >({
      query: (arg?: ShopifyCountRequest) => ({
        url: '/shopify/variants/count',
        method: 'GET',
        ...(arg ? { params: arg } : {}),
      }),
      serializeQueryArgs: ({ queryArgs = {} }) => {
        const sortedQueryArgs = Object.keys(queryArgs)
          .sort()
          .reduce(
            (result: any, key: any) => {
              result[key] = queryArgs?.[key as keyof ShopifyCountRequest]
              return result
            },
            {} as typeof queryArgs,
          )

        return `/shopify/variants/count:${JSON.stringify(sortedQueryArgs)}`
      },
    }),
    shopifyImportedVariantCount: builder.query<number, void>({
      query: () => ({
        url: '/shopify/variants/count/imported',
        method: 'GET',
      }),
    }),
    importShopifyProducts: builder.mutation<any, ShopifyProductImportRequest>({
      query: (body) => ({
        url: '/shopify/products',
        method: 'POST',
        body: body,
      }),
      invalidatesTags: (result, error) =>
        error
          ? []
          : [
              'Products',
              'ProductCount',
              'Sidebar',
              'Me',
              'BatchImport',
              'BatchImportLatest',
              'PriceApiMatchStatus',
            ],
    }),
    smartPriceLiveProducts: builder.query<
      PaginatedResponse<Product>,
      PaginatedQueryParams
    >({
      query: (arg: PaginatedQueryParams) => ({
        url: '/products',
        method: 'GET',
        params: arg,
        cache: 'no-cache',
      }),
    }),
    listProducts: builder.query<
      PaginatedResponse<Product>,
      PaginatedQueryParams
    >({
      query: (arg: PaginatedQueryParams) => ({
        url: '/products',
        method: 'GET',
        params: arg,
      }),
    }),
    getProductDetail: builder.query<Product, number>({
      query: (id: number) => ({
        url: `/products/${id}`,
        method: 'GET',
      }),
    }),
    getProductSites: builder.query<ProductSite[], number>({
      query: (id: number) => ({
        url: `/products/${id}/sites`,
        method: 'GET',
      }),
    }),

    listSavedProductFilters: builder.query<Filter[], unknown>({
      query: () => ({
        url: `/filters`,
        method: 'GET',
      }),
      providesTags: ['Filters'],
    }),
    addQuickFilter: builder.mutation<void, { name: string; url: string }>({
      query: (body) => ({
        url: `/filters`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Filters'],
    }),
    updateQuickFilter: builder.mutation<void, { id: number; url: string }>({
      query: ({ id, url }) => ({
        url: `/filters/${id}`,
        method: 'PATCH',
        body: { url },
      }),
      invalidatesTags: ['Filters'],
    }),
    deleteFilter: builder.mutation<any, any>({
      query: (id) => ({
        url: `/filters/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Filters'],
    }),
    createProduct: builder.mutation<Product, CreateProductDto>({
      query: (body) => ({
        url: '/products',
        method: 'POST',
        body: body,
      }),
      invalidatesTags: (result, error) =>
        error ? [] : ['Products', 'ProductCount', 'Sidebar', 'Me'],
    }),
    editProduct: builder.mutation<
      Product,
      { body: CreateProductDto; id: number }
    >({
      query: ({ body, id }) => ({
        url: `/products/${id}`,
        method: 'PATCH',
        body: body,
      }),
      invalidatesTags: ['Products', 'ProductCount'],
    }),
    createFeedTarget: builder.mutation<ProductSourceFeed, FeedTargetBody>({
      query: (body) => ({
        url: `/conveyor/target`,
        method: 'POST',
        body: body,
      }),
      // Creating the target now completes setup on its own — the mapping goes
      // with it — so nothing later refreshes the panel. `Me` carries
      // conveyorTarget, which is what populates selectedProductSource.
      //
      // BatchImport too: saving a feed starts its first import, so a run
      // exists the moment this returns. Without it, Recent Imports kept
      // showing the list it had loaded before setup and the user had to
      // reload the page to see the import they had just started.
      invalidatesTags: ['Me', 'FeedTargetMe', 'BatchImport'],
    }),
    updateFeedTarget: builder.mutation<
      ProductSourceFeed,
      { id: number; body: Partial<FeedTargetBody> }
    >({
      query: ({ id, body }) => ({
        url: `/conveyor/target/${id}`,
        method: 'PATCH',
        body: body,
      }),
      invalidatesTags: ['Me', 'FeedTargetMe'],
    }),
    getFeedSchemaMappingSuggestion: builder.query<
      Record<string, string>,
      { fields: string[]; samples?: Record<string, string> }
    >({
      // `samples` carries the first-record value per column. Names alone leave
      // an opaque header like `col_1` unmappable no matter how good the
      // suggester is; the value identifies it.
      query: ({ fields, samples }) => ({
        url: `/conveyor/mapping/preview`,
        method: 'POST',
        body: { fields, samples },
      }),
    }),
    getFeedTargetForMe: builder.query<ProductSourceFeed, void>({
      query: () => ({
        url: `/conveyor/target/me`,
        method: 'GET',
      }),
      providesTags: ['FeedTargetMe'],
    }),
    visitFeedTargetForMe: builder.mutation<void, void>({
      query: () => ({
        url: `/conveyor/target/me/visit`,
        method: 'POST',
      }),
    }),
    // First record of a feed that has not been saved yet, so the mapping
    // screen can be built before anything is persisted. The saved-target
    // equivalent is visitFeedTargetForMe.
    previewFeedTarget: builder.mutation<
      {
        // Feed format resolved server-side while reading the feed; persisted
        // as the target's contentType. Previously came from the separate
        // validation call.
        format?: string
        contentType?: string
        data?: { index: number; name: string; value: unknown }[]
      },
      {
        url: string
        authType?: string | null
        authPayload?: Record<string, unknown> | null
      }
    >({
      query: (body) => ({
        url: `/conveyor/target/preview`,
        method: 'POST',
        body,
      }),
    }),
    runManualFeedSync: builder.mutation<{ productToolQueueId: number }, void>({
      query: () => ({
        url: `/conveyor/target/me/sync`,
        method: 'POST',
      }),
      // The run exists as soon as this returns, so Recent Imports is stale the
      // moment it does.
      invalidatesTags: ['FeedTargetMe', 'BatchImport'],
    }),
    getFeedFields: builder.query({
      query: () => ({
        url: `/conveyor/field`,
        method: 'GET',
      }),
      providesTags: ['FeedFields'],
    }),
    saveFeedFieldMapping: builder.mutation<
      void,
      {
        id: number
        mapping: {
          fieldId: number
          name: string
          value?: any
        }[]
      }
    >({
      query: ({ id, mapping }) => ({
        url: `/conveyor/target/${id}/fields`,
        method: 'POST',
        body: mapping,
      }),
      invalidatesTags: ['Me'],
    }),
    getImportActivity: builder.query<
      PaginatedResponse<BatchImportActivity>,
      PaginatedQueryParams & { type: ('apt' | 'upt' | 'upt-c' | 'conveyor')[] }
    >({
      query: (args) => ({
        url: '/products/import/latest',
        method: 'GET',
        params: args,
      }),
      providesTags: ['BatchImport'],
    }),
    getLatestImportActivity: builder.query<LatestBatchImportActivity, number>({
      query: (id) => ({
        url: `/products/import/${id}`,
        method: 'GET',
      }),
      providesTags: ['BatchImportLatest'],
    }),
    downloadImportedFile: builder.mutation<any, { id: number; type: string }>({
      query: ({ id, type }) => ({
        url: `/products/import/download/${id}`,
        method: 'POST',
        body: { type },
        responseHandler: async (response) => {
          if (![200, 201].includes(response.status)) {
            throw new Error('Failed to download file')
          }

          const contentDisposition: string | null = response.headers.get(
            'Content-Disposition',
          )
          const filename = contentDisposition?.replaceAll('"', '').trim()

          const link = window.URL.createObjectURL(await response.blob())

          saveAs(link, filename)
        },
        cache: 'no-cache',
      }),
    }),
    createImport: builder.mutation<{ status: boolean }, CreateImportRequest>({
      query: (body) => ({
        url: '/products/import',
        method: 'POST',
        body,
      }),
      invalidatesTags: [
        'BatchImport',
        'PriceApiMatchStatus',
        'Products',
        'ProductCount',
        'BatchImportLatest',
      ],
    }),
    priceApiMatchStatus: builder.query<PriceAPIResponse, number>({
      query: (id) => ({
        url: `/products/import/match-status/${id}`,
        method: 'GET',
      }),
      providesTags: ['PriceApiMatchStatus'],
    }),
    cancelImport: builder.mutation<void, number>({
      query: (id) => ({
        url: `/products/import/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [
        'BatchImport',
        'BatchImportLatest',
        'PriceApiMatchStatus',
      ],
    }),
    downloadProductsFile: builder.mutation<
      any,
      { type?: 'product' | 'competitor' }
    >({
      query: (args) => ({
        url: `/products/download`,
        params: args,
        method: 'POST',
        responseHandler: async (response) => {
          if (![200, 201].includes(response.status)) {
            throw new Error('Failed to download file')
          }

          const contentDisposition: string | null = response.headers.get(
            'Content-Disposition',
          )

          const filename = contentDisposition
            ?.split(';')
            ?.find((n) => n.includes('filename='))
            ?.replace('filename=', '')
            ?.replaceAll('"', '')
            .trim()

          const link = window.URL.createObjectURL(await response.blob())

          saveAs(link, filename)
        },
        cache: 'no-cache',
      }),
    }),
    bookmark: builder.mutation<Product, number>({
      query: (id) => ({
        url: `/products/${id}/favorite`,
        method: 'POST',
      }),
    }),
    toggleExclusion: builder.mutation<Product, number>({
      query: (id) => ({
        url: `/products/${id}/exclusion`,
        method: 'POST',
      }),
      invalidatesTags: ['Products', 'ProductCount'],
    }),
    reportUrl: builder.mutation<{ isAlreadyReported: any }, any>({
      query: ({ id, urlId, reason, details }) => ({
        url: `/products/${id}/report-url/${urlId}`,
        method: 'POST',
        body: { reason: reason, details: details },
      }),
    }),
    getReport: builder.query<boolean, any>({
      query: ({ id, urlId }) => ({
        url: `/products/${id}/report-url/${urlId}`,
        method: 'GET',
      }),
    }),
    addUrl: builder.mutation<any, { id: number; url: string }>({
      query: ({ id, url }) => ({
        url: `/products/${id}/add-url`,
        method: 'POST',
        body: { url: url },
      }),
      invalidatesTags: (result, error) =>
        error
          ? []
          : ['Products', 'ProductCount', 'BreakDownInfo', 'Sidebar', 'Me'],
    }),
    fastFetchNotify: builder.mutation<
      any,
      { id: number; email_subscription: number; price?: number }
    >({
      query: ({ id, email_subscription, price }) => ({
        url: `/products/${id}/fast-fetch`,
        method: 'POST',
        body: { email_subscription, price },
      }),
    }),
    editUrl: builder.mutation<any, any>({
      query: ({ id, urlId, url }) => ({
        url: `/products/${id}/edit-url/${urlId}`,
        method: 'POST',
        body: { url: url },
      }),
      invalidatesTags: ['Products', 'ProductCount', 'BreakDownInfo'],
    }),
    deleteUrl: builder.mutation<any, any>({
      query: ({ id, urlId, ...rest }) => ({
        url: `/products/${id}/delete-url/${urlId}`,
        method: 'DELETE',
        body: rest,
      }),
      invalidatesTags: ['Products', 'ProductCount', 'BreakDownInfo', 'Me'],
    }),
    deleteProductTag: builder.mutation<any, any>({
      query: (id) => ({
        url: `/products/tags/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Products', 'ProductCount'],
    }),
    productsManage: builder.query<
      PaginatedResponse<Product>,
      PaginatedQueryParams
    >({
      query: (arg: PaginatedQueryParams) => ({
        url: '/products/manage',
        method: 'GET',
        params: arg,
      }),
      providesTags: ['Products'],
    }),
    productsManageIsFavorite: builder.query<
      PaginatedResponse<Product>,
      PaginatedQueryParams
    >({
      query: (arg: PaginatedQueryParams) => ({
        url: '/products/manage?is_favorite=true',
        method: 'GET',
        params: arg,
      }),
    }),
    deleteProducts: builder.mutation<
      any,
      {
        body: {
          selected_rows: Array<number>
          unselected_rows: Array<number>
          select_all: boolean
        }
        params?: ProductFilter
      }
    >({
      query: ({ body, params }) => ({
        url: '/products/delete',
        method: 'POST',
        params: params,
        body,
      }),
      invalidatesTags: ['Products', 'ProductCount', 'Me'],
    }),
    moveProductsBrand: builder.mutation<
      any,
      {
        body: {
          selected_rows: Array<number>
          unselected_rows: Array<number>
          select_all: boolean
          category_id?: number
          category_name?: string
        }
        params?: ProductFilter
      }
    >({
      query: ({ body, params }) => ({
        url: 'products/move/brand',
        method: 'POST',
        params: params,
        body,
      }),
      invalidatesTags: ['Products', 'ProductCount'],
    }),
    moveProductsCategory: builder.mutation<
      any,
      {
        body: {
          selected_rows: Array<number>
          unselected_rows: Array<number>
          select_all: boolean
          category_id?: number
          category_name?: string
        }
        params?: ProductFilter
      }
    >({
      query: ({ body, params }) => ({
        url: 'products/move/category',
        method: 'POST',
        params: params,
        body,
      }),
      invalidatesTags: ['Products', 'ProductCount'],
    }),
    getProductPricesHistory: builder.query<
      any,
      {
        id: number
        params: {
          siteIds: Array<any>
          dateBefore: string
          dateAfter: string
        }
      }
    >({
      query: ({ id, params }) => ({
        url: `history/products/${id}/prices`,
        method: 'GET',
        params: {
          ...params,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
      }),
    }),
    startExport: builder.mutation<any, any>({
      query: (body) => ({
        url: '/report/start-export',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Export', 'ExportCount', 'ExportLatest'],
    }),
    getLatestExportStatus: builder.query<any, void>({
      query: () => ({
        url: '/report/export/status/latest',
        method: 'GET',
      }),
      providesTags: () => ['ExportLatest'],
    }),
    getExportCount: builder.query<{ count: number }, void>({
      query: () => ({
        url: '/report/export/count',
        method: 'GET',
      }),
      providesTags: ['ExportCount'],
    }),
    startHistoryExport: builder.mutation<
      any,
      {
        export_type:
          | 'zip_history_vertical'
          | 'xlsx_history_vertical'
          | 'csv_history_vertical'
        start_date?: string
        end_date?: string
        product_id: number
      }
    >({
      query: (body) => ({
        url: 'products/start-history-export',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Export', 'ExportLatest'],
    }),
    productHistoryAtDate: builder.query<any, { id: number; date: string }>({
      query: ({ id, date }) => ({
        url: `/history/products/${id}/${date}`,
        method: 'GET',
        // Calendar View groups checks into "date" the same way Chart View
        // does — by the viewer's own browser clock — so the backend needs
        // this zone to decide day boundaries consistently with the chart.
        params: {
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
      }),
    }),
    manageDeleteProducts: builder.mutation<
      any,
      {
        body: {
          selected_rows: Array<number>
          unselected_rows: Array<number>
          select_all: boolean
          filter?: ProductFilter
          expected_count?: number
        }
      }
    >({
      query: ({ body }) => ({
        url: 'manage/product/delete',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Products', 'ProductCount', 'Me'],
    }),
  }),
})

export const {
  useProductsQuery,
  useTotalProductCountQuery,
  useLazyShopifyProductsQuery,
  useLazyShopifyVariantCountQuery,
  useLazyShopifyImportedVariantCountQuery,
  useImportShopifyProductsMutation,
  useLazySmartPriceLiveProductsQuery,
  useLazyProductsQuery,
  useListProductsQuery,
  useGetProductDetailQuery,
  useGetProductSitesQuery,
  useCreateProductMutation,
  useEditProductMutation,
  useListSavedProductFiltersQuery,
  useAddQuickFilterMutation,
  useUpdateQuickFilterMutation,
  useDeleteFilterMutation,
  useCreateFeedTargetMutation,
  useUpdateFeedTargetMutation,
  useLazyGetFeedSchemaMappingSuggestionQuery,
  useGetFeedTargetForMeQuery,
  useVisitFeedTargetForMeMutation,
  usePreviewFeedTargetMutation,
  useRunManualFeedSyncMutation,
  useLazyGetFeedFieldsQuery,
  useSaveFeedFieldMappingMutation,
  useLazyGetImportActivityQuery,
  useGetLatestImportActivityQuery,
  useLazyGetLatestImportActivityQuery,
  useCreateImportMutation,
  usePriceApiMatchStatusQuery,
  useCancelImportMutation,
  useBookmarkMutation,
  useToggleExclusionMutation,
  useAddUrlMutation,
  useFastFetchNotifyMutation,
  useEditUrlMutation,
  useReportUrlMutation,
  useLazyGetReportQuery,
  useDeleteUrlMutation,
  useDeleteProductTagMutation,
  useLazyProductsManageQuery,
  useLazyProductsManageIsFavoriteQuery,
  useDeleteProductsMutation,
  useMoveProductsBrandMutation,
  useMoveProductsCategoryMutation,
  useLazyGetProductPricesHistoryQuery,
  useStartHistoryExportMutation,
  useLazyProductHistoryAtDateQuery,
  useGetLatestExportStatusQuery,
  useStartExportMutation,
  useGetExportCountQuery,
  useDownloadImportedFileMutation,
  useDownloadProductsFileMutation,
  useManageDeleteProductsMutation,
} = productApi
